import { useCallback, useEffect, useMemo, useState } from "react";
import {
  disablePartnerInventoryDataSourceSchedule,
  enablePartnerInventoryDataSourceSchedule,
  listPartnerInventoryDataSources,
  runPartnerInventoryDataSource,
} from "../../../api/partnerPortalApi";
import { formatDateTime } from "../../midpoint/admin/users/adminUserFormatters";
import { WEATHER_SCHEDULE_INTERVALS } from "../../../services/midpointApi";

const POLL_INTERVAL_MS = 4000;

const SCHEDULE_OPTIONS = WEATHER_SCHEDULE_INTERVALS.filter((entry) => entry.minutes != null);

function scheduleLabel(source) {
  if (!source.scheduleEnabled || source.scheduleIntervalMinutes == null) {
    return "Manual only";
  }

  const match = SCHEDULE_OPTIONS.find((entry) => entry.minutes === source.scheduleIntervalMinutes);
  return match?.label || `Every ${source.scheduleIntervalMinutes} min`;
}

function healthLabel(source) {
  if (!source.enabled) return "Disabled";
  if (source.isRunning || source.health === "running") return "Running";
  if (source.health === "failed") return "Failed";
  if (source.health === "healthy") return "Healthy";
  if (source.health === "warning") return "Needs attention";
  if (source.health === "never_run") return "Never synced";
  return source.lastStatus || "Unknown";
}

function healthTone(source) {
  if (!source.enabled) return "disabled";
  if (source.isRunning || source.health === "running") return "running";
  if (source.health === "failed") return "failed";
  if (source.health === "healthy") return "ready";
  return "warning";
}

function formatActionError(error) {
  if (!error) return "Action failed.";
  const blockers = error.blockers;
  if (Array.isArray(blockers) && blockers.length) {
    return blockers
      .map((item) => (typeof item === "string" ? item : item.message || item.code))
      .join(" ");
  }
  return error.message || "Action failed.";
}

function sourceMetaValue(value) {
  if (!value) return "—";
  return String(value);
}

export default function PartnerInventoryDataSourcesSection({ active = true }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [busySourceCode, setBusySourceCode] = useState("");
  const [scheduleSource, setScheduleSource] = useState(null);
  const [scheduleMinutes, setScheduleMinutes] = useState(1440);

  const loadSources = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError("");
      const result = await listPartnerInventoryDataSources();
      setSources(result.sources || []);
    } catch (err) {
      setSources([]);
      setError(err.message || "Unable to load vehicle data sources.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    loadSources();
    return undefined;
  }, [active, loadSources]);

  const hasRunningSource = useMemo(
    () => sources.some((source) => source.isRunning || source.health === "running"),
    [sources]
  );

  useEffect(() => {
    if (!active || !hasRunningSource) return undefined;

    const timer = window.setInterval(() => {
      loadSources({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [active, hasRunningSource, loadSources]);

  const handleRun = async (sourceCode) => {
    try {
      setBusySourceCode(sourceCode);
      setActionError("");
      setActionMessage("");
      const result = await runPartnerInventoryDataSource(sourceCode);
      setActionMessage(result.message || "Sync started.");
      await loadSources({ silent: true });
    } catch (err) {
      setActionError(formatActionError(err));
    } finally {
      setBusySourceCode("");
    }
  };

  const openSchedule = (source) => {
    setScheduleSource(source);
    setScheduleMinutes(source.scheduleIntervalMinutes || 1440);
    setActionError("");
    setActionMessage("");
  };

  const closeSchedule = () => {
    setScheduleSource(null);
  };

  const handleSaveSchedule = async () => {
    if (!scheduleSource) return;

    try {
      setBusySourceCode(scheduleSource.sourceCode);
      setActionError("");
      setActionMessage("");

      if (scheduleMinutes == null) {
        const result = await disablePartnerInventoryDataSourceSchedule(scheduleSource.sourceCode);
        setActionMessage(result.message || "Schedule disabled.");
      } else {
        const result = await enablePartnerInventoryDataSourceSchedule(
          scheduleSource.sourceCode,
          scheduleMinutes
        );
        setActionMessage(result.message || "Schedule saved.");
      }

      closeSchedule();
      await loadSources({ silent: true });
    } catch (err) {
      setActionError(formatActionError(err));
    } finally {
      setBusySourceCode("");
    }
  };

  if (!active) {
    return null;
  }

  return (
    <section className="partner-inventory-data-sources">
      <div className="partner-panel partner-inventory-data-sources__header-panel">
        <div className="partner-panel__header">
          <div>
            <h3>AutoMapa data sources</h3>
            <p className="muted partner-health-section-intro">
              All configured ingest pipelines that power AutoMapa inventory, reference pricing, and
              repair benchmarks. Review sync status, trigger manual runs, and configure schedules.
            </p>
          </div>
          <button
            type="button"
            className="partner-dashboard-btn"
            onClick={() => loadSources()}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {error ? <p className="admin-data-health-error">{error}</p> : null}
        {actionError ? <p className="admin-data-health-error">{actionError}</p> : null}
        {actionMessage ? <p className="partner-inventory-data-sources__message">{actionMessage}</p> : null}
      </div>

      {loading && !sources.length ? (
        <div className="partner-panel partner-health-loading">Loading AutoMapa data sources…</div>
      ) : null}

      {!loading && !sources.length && !error ? (
        <div className="partner-panel">
          <p className="muted">No AutoMapa data sources are configured yet.</p>
        </div>
      ) : null}

      {sources.length ? (
        <div className="partner-inventory-data-sources__list">
          {sources.map((source) => {
            const isBusy = busySourceCode === source.sourceCode;
            const canRun = source.supportsRunNow && source.enabled;

            return (
              <article key={source.sourceCode} className="partner-panel partner-inventory-data-source-card">
                <div className="partner-inventory-data-source-card__head">
                  <div>
                    <h4>{source.sourceName || source.sourceCode}</h4>
                    <p className="partner-inventory-data-source-card__code">{source.sourceCode}</p>
                  </div>
                  <span
                    className={`partner-health-status partner-health-status--${healthTone(source)}`}
                  >
                    {healthLabel(source)}
                  </span>
                </div>

                <dl className="partner-inventory-data-source-card__meta">
                  <div>
                    <dt>Last sync</dt>
                    <dd>{formatDateTime(source.lastRunAt || source.lastSuccessAt) || "—"}</dd>
                  </div>
                  <div>
                    <dt>Next run</dt>
                    <dd>{formatDateTime(source.nextRunAt) || "—"}</dd>
                  </div>
                  <div>
                    <dt>Schedule</dt>
                    <dd>{scheduleLabel(source)}</dd>
                  </div>
                  <div>
                    <dt>Active listings</dt>
                    <dd>
                      {source.recordCount?.toLocaleString?.() ?? source.activeListingCount ?? "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Channel</dt>
                    <dd>{sourceMetaValue(source.inventoryChannel)}</dd>
                  </div>
                  <div>
                    <dt>Data role</dt>
                    <dd>{sourceMetaValue(source.dataRole)}</dd>
                  </div>
                  <div>
                    <dt>Access</dt>
                    <dd>{sourceMetaValue(source.accessMethod)}</dd>
                  </div>
                  <div>
                    <dt>Geography</dt>
                    <dd>{sourceMetaValue(source.geographyScope)}</dd>
                  </div>
                  <div>
                    <dt>Source URL</dt>
                    <dd>
                      {source.baseUrl ? (
                        <a href={source.baseUrl} target="_blank" rel="noreferrer">
                          {source.baseUrl}
                        </a>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                </dl>

                {source.lastErrorMessage ? (
                  <p className="partner-inventory-data-source-card__error">{source.lastErrorMessage}</p>
                ) : null}

                <div className="partner-inventory-data-source-card__actions">
                  <button
                    type="button"
                    className="partner-dashboard-btn partner-dashboard-btn--primary"
                    disabled={!canRun || isBusy || source.isRunning}
                    onClick={() => handleRun(source.sourceCode)}
                  >
                    {source.isRunning ? "Running…" : isBusy ? "Starting…" : "Run now"}
                  </button>
                  <button
                    type="button"
                    className="partner-dashboard-btn"
                    disabled={!source.supportsSchedule || isBusy}
                    onClick={() => openSchedule(source)}
                  >
                    Schedule
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {scheduleSource ? (
        <div className="partner-inventory-data-sources__schedule" role="dialog" aria-label="Source schedule">
          <div className="partner-inventory-data-sources__schedule-card">
            <h4>Schedule for {scheduleSource.sourceName || scheduleSource.sourceCode}</h4>
            <label className="partner-inventory-data-sources__schedule-field">
              <span>Frequency</span>
              <select
                value={scheduleMinutes ?? "manual"}
                onChange={(event) => {
                  const value = event.target.value;
                  setScheduleMinutes(value === "manual" ? null : Number(value));
                }}
              >
                <option value="manual">Manual only</option>
                {SCHEDULE_OPTIONS.map((option) => (
                  <option key={option.minutes} value={option.minutes}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="partner-inventory-data-sources__schedule-actions">
              <button
                type="button"
                className="partner-dashboard-btn partner-dashboard-btn--primary"
                disabled={busySourceCode === scheduleSource.sourceCode}
                onClick={handleSaveSchedule}
              >
                Save schedule
              </button>
              <button type="button" className="partner-dashboard-btn" onClick={closeSchedule}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
